/**
 * WebSocket connection manager.
 *
 * Uses Node.js native WebSocket (available in Node 22+).
 * Authenticates via SDK helpers, routes typed messages to the consumer.
 */

import {
  buildWebSocketUrl,
  createAuthMessage,
  parseServerMessage,
  isMessageType,
  type WebSocketServerMessage,
  type WsAuthResultMessage,
  type WsTaskUpdateMessage,
  type WsBatchCompleteMessage,
  type WsErrorMessage,
  type WsPresenceUpdateMessage,
  type WsNotificationResponse,
  type WsResponseCompleteMessage,
} from '@arbidocs/client'

const AUTH_TIMEOUT_MS = 10_000
const MAX_BACKOFF_MS = 30_000

/**
 * Thrown when a WebSocket connection is rejected for authentication reasons
 * (the server returned `auth_result { success: false }`, or no token was
 * available to send). The reconnect loop uses this to decide whether to
 * re-authenticate before the next attempt, versus treating the failure as a
 * transient network drop.
 */
export class WebSocketAuthError extends Error {
  constructor(reason: string) {
    super(`WebSocket auth failed: ${reason}`)
    this.name = 'WebSocketAuthError'
  }
}

export interface WsConnection {
  close: () => void
}

export interface ConnectOptions {
  baseUrl: string
  accessToken: string
  onMessage: (msg: WebSocketServerMessage) => void
  onClose?: (code: number, reason: string) => void
  /**
   * Called once the server accepts auth (`auth_result { success: true }`). The
   * full message is passed so callers can read fields like `new_messages`. The
   * auth handshake itself is owned by the transport — this is a notification.
   */
  onAuthenticated?: (msg: WsAuthResultMessage) => void
}

export interface ReconnectOptions extends ConnectOptions {
  /**
   * Max consecutive reconnect attempts after a drop before giving up and
   * calling `onReconnectFailed`. Pass `Infinity` for an unattended daemon that
   * must keep trying indefinitely (backoff still caps at {@link MAX_BACKOFF_MS}).
   * Default 10.
   */
  maxRetries?: number
  initialDelayMs?: number
  /**
   * Resolve the access token before each (re)connect attempt. Defaults to the
   * static `accessToken`. Provide this so a token refreshed elsewhere (e.g. by
   * auto-relogin on a REST 401) is picked up on reconnect instead of reusing a
   * stale token captured when the listener first started.
   */
  getAccessToken?: () => string | null | Promise<string | null>
  /**
   * Re-authenticate when a reconnect attempt is rejected for auth reasons
   * (expired/invalid token — see {@link WebSocketAuthError}). Should refresh the
   * token in whatever store `getAccessToken` reads from; the return value is
   * ignored. Best-effort: errors are swallowed and normal backoff continues.
   */
  refreshAuth?: () => Promise<string | null>
  onReconnecting?: (attempt: number, maxRetries: number) => void
  onReconnected?: () => void
  onReconnectFailed?: () => void
  /**
   * Decide whether to reconnect after a given close. Return `false` to stop —
   * e.g. a server-initiated single-session eviction (code 1008), where
   * reconnecting would just get kicked again. Default: always reconnect.
   */
  shouldReconnect?: (code: number, reason: string) => boolean
}

export interface ReconnectableWsConnection {
  close: () => void
}

/**
 * Open a WebSocket, authenticate, and start routing messages.
 *
 * Resolves after a successful `auth_result`, rejects on auth failure or timeout.
 * Subsequent messages are dispatched to `onMessage`.
 */
export function connectWebSocket(options: ConnectOptions): Promise<WsConnection> {
  const { baseUrl, accessToken, onMessage, onClose, onAuthenticated } = options
  const url = buildWebSocketUrl(baseUrl)

  return new Promise<WsConnection>((resolve, reject) => {
    const ws = new WebSocket(url)
    let authenticated = false

    const timeout = setTimeout(() => {
      if (!authenticated) {
        ws.close()
        reject(new Error('WebSocket auth timed out'))
      }
    }, AUTH_TIMEOUT_MS)

    ws.addEventListener('open', () => {
      ws.send(createAuthMessage(accessToken))
    })

    ws.addEventListener('message', (event) => {
      const data = typeof event.data === 'string' ? event.data : String(event.data)
      const msg = parseServerMessage(data)
      if (!msg) return

      if (!authenticated) {
        if (isMessageType<WsAuthResultMessage>(msg, 'auth_result')) {
          clearTimeout(timeout)
          if (msg.success) {
            authenticated = true
            onAuthenticated?.(msg)
            resolve({ close: () => ws.close() })
          } else {
            ws.close()
            reject(new WebSocketAuthError(msg.reason || 'unknown'))
          }
        }
        return
      }

      onMessage(msg)
    })

    ws.addEventListener('close', (event) => {
      clearTimeout(timeout)
      if (!authenticated) {
        reject(new Error(`WebSocket closed before auth (code ${event.code})`))
        return
      }
      onClose?.(event.code, event.reason)
    })

    ws.addEventListener('error', () => {
      // The close event will fire after error, so we handle rejection there.
      // This prevents unhandled rejection from the error event itself.
    })
  })
}

/**
 * Connect WebSocket with automatic reconnection on disconnect.
 *
 * The initial connection is auth-symmetric with the reconnect loop: if the first
 * attempt is rejected for auth reasons and `refreshAuth` is provided, it re-logs
 * in and retries once with the fresh token before throwing. Any other failure —
 * or a still-failing retry — throws to the caller.
 * After successful auth, disconnects trigger exponential backoff reconnection:
 * - a fresh token is resolved before every attempt (see `getAccessToken`), so a
 *   token rotated elsewhere is picked up rather than reusing a stale one;
 * - an attempt rejected for auth reasons triggers `refreshAuth` (re-login) so
 *   the next attempt uses a new token;
 * - retries continue up to `maxRetries` (pass `Infinity` for a daemon that must
 *   never give up). Only after exhausting a finite limit is `onReconnectFailed`
 *   called.
 *
 * Call close() on the returned handle to stop reconnection attempts.
 */
export async function connectWithReconnect(
  options: ReconnectOptions
): Promise<ReconnectableWsConnection> {
  const {
    maxRetries = 10,
    initialDelayMs = 1000,
    getAccessToken,
    refreshAuth,
    onReconnecting,
    onReconnected,
    onReconnectFailed,
    onClose,
    onMessage,
    onAuthenticated,
    shouldReconnect,
    baseUrl,
    accessToken,
  } = options

  const resolveToken = getAccessToken ?? (() => accessToken)

  let closed = false
  let cancelDelay: (() => void) | null = null
  let currentConnection: WsConnection | null = null

  // Open a single connection using the freshest available token. Rejects with
  // WebSocketAuthError when no token is available or the server rejects auth, so
  // the reconnect loop can tell "needs re-login" apart from a network drop.
  const connectOnce = async (): Promise<WsConnection> => {
    const token = await resolveToken()
    if (!token) throw new WebSocketAuthError('no access token available')
    return connectWebSocket({
      baseUrl,
      accessToken: token,
      onMessage,
      onAuthenticated,
      onClose: (code, reason) => {
        currentConnection = null
        onClose?.(code, reason)
        if (!closed && (shouldReconnect?.(code, reason) ?? true)) void scheduleReconnect()
      },
    })
  }

  const scheduleReconnect = async () => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      if (closed) return

      const delay = Math.min(initialDelayMs * Math.pow(2, attempt - 1), MAX_BACKOFF_MS)
      onReconnecting?.(attempt, maxRetries)

      // Cancellable backoff — close() resolves this immediately so shutdown
      // doesn't wait out the delay (and doesn't leak a pending timer/promise).
      await new Promise<void>((resolve) => {
        const timer = setTimeout(resolve, delay)
        cancelDelay = () => {
          clearTimeout(timer)
          resolve()
        }
      })
      cancelDelay = null

      if (closed) return

      try {
        currentConnection = await connectOnce()
        onReconnected?.()
        return
      } catch (err) {
        // Token expired/invalid — re-login so the next attempt uses a fresh
        // token (resolveToken reads it back from the refreshed session).
        // Best-effort; on failure we fall through to normal backoff.
        if (err instanceof WebSocketAuthError && refreshAuth) {
          await refreshAuth().catch(() => {})
        }
      }
    }

    if (!closed) onReconnectFailed?.()
  }

  // Initial connection. Symmetric with the reconnect loop: an auth-rejected
  // first attempt triggers refreshAuth (re-login) and ONE retry with the fresh
  // token, so the transport fully owns auth and callers need no re-login
  // fallback of their own. A non-auth failure — or a still-failing retry —
  // propagates to the caller, who decides how to surface it.
  try {
    currentConnection = await connectOnce()
  } catch (err) {
    if (!(err instanceof WebSocketAuthError) || !refreshAuth) throw err
    await refreshAuth().catch(() => {})
    currentConnection = await connectOnce()
  }

  return {
    close: () => {
      closed = true
      cancelDelay?.()
      currentConnection?.close()
      currentConnection = null
    },
  }
}

// ── Document processing waiter ───────────────────────────────────────────────

/**
 * Document statuses that mean processing is finished (success or otherwise).
 *
 * Anything in this set should cause `--watch` loops to stop tracking the doc:
 * - `completed`   — happy path, content extracted and indexed
 * - `failed`      — processing errored
 * - `empty`       — parser found no extractable content (e.g. blank scan)
 * - `low-content` — parser extracted only 1-2 chunks (minimal content)
 * - `skipped`     — server skipped (duplicate, unsupported, quota, etc.)
 *
 * Treating anything else as terminal will leave watchers hung when the parser
 * legitimately bails out on degenerate input (we hit this on the bench script
 * with random-byte PDFs, where ~1 in 6 docs ended at `empty`).
 */
export const DOC_TERMINAL_STATUSES: ReadonlySet<string> = new Set([
  'completed',
  'failed',
  'skipped',
  'empty',
  'low-content',
])

export interface DocumentWaiterOptions {
  /** Base URL of the ARBI server (e.g. "https://app.arbi.city"). */
  baseUrl: string
  /** JWT access token for WebSocket authentication. */
  accessToken: string
  /** Optional callback invoked for every task_update on tracked documents. */
  onStatus?: (msg: WsTaskUpdateMessage) => void
}

export interface DocumentWaiter {
  /** Register document IDs to track. Can be called multiple times. */
  addDocIds(ids: string[]): void
  /** Block until all tracked documents reach terminal status. */
  waitUntilDone(timeoutMs?: number): Promise<Map<string, string>>
  /** Close the WebSocket without waiting. */
  close(): void
}

/**
 * Open a WebSocket and start collecting document status events.
 *
 * Call this BEFORE uploading so no events are missed. Then call `addDocIds()`
 * once you know the IDs, and `waitUntilDone()` to block until all reach a
 * terminal status (completed, failed, or skipped).
 *
 * Mirrors the backend SDK's `ArbiWebSocket.wait_for_docs()` pattern.
 *
 * @example
 * ```ts
 * const waiter = createDocumentWaiter({ baseUrl, accessToken })
 * const result = await uploadDocuments(...)
 * waiter.addDocIds(result.doc_ext_ids)
 * const statuses = await waiter.waitUntilDone(120_000)
 * ```
 */
export function createDocumentWaiter(options: DocumentWaiterOptions): DocumentWaiter {
  const { baseUrl, accessToken, onStatus } = options

  const tracked = new Set<string>()
  const results = new Map<string, string>()
  // Buffer terminal events so we don't lose them if they arrive before addDocIds()
  const terminalBuffer = new Map<string, string>()
  let resolveWait: ((results: Map<string, string>) => void) | null = null
  let authenticated = false

  const url = buildWebSocketUrl(baseUrl)
  const ws = new WebSocket(url)

  const checkDone = () => {
    if (resolveWait && [...tracked].every((id) => results.has(id))) {
      resolveWait(results)
      resolveWait = null
    }
  }

  ws.addEventListener('open', () => {
    ws.send(createAuthMessage(accessToken))
  })

  ws.addEventListener('message', (event) => {
    const data = typeof event.data === 'string' ? event.data : String(event.data)
    const msg = parseServerMessage(data)
    if (!msg) return

    if (!authenticated) {
      if (msg.type === 'auth_result') {
        authenticated = (msg as { success?: boolean }).success === true
      }
      return
    }

    if (isMessageType<WsTaskUpdateMessage>(msg, 'task_update')) {
      if (tracked.has(msg.doc_ext_id)) {
        onStatus?.(msg)
      }

      if (DOC_TERMINAL_STATUSES.has(msg.status as string)) {
        terminalBuffer.set(msg.doc_ext_id, msg.status)
        if (tracked.has(msg.doc_ext_id)) {
          results.set(msg.doc_ext_id, msg.status)
          checkDone()
        }
      }
    }
  })

  return {
    addDocIds(ids: string[]) {
      for (const id of ids) {
        tracked.add(id)
        // Replay any terminal event that arrived before tracking started
        const buffered = terminalBuffer.get(id)
        if (buffered) {
          results.set(id, buffered)
        }
      }
      checkDone()
    },

    waitUntilDone(timeoutMs = 120_000): Promise<Map<string, string>> {
      if ([...tracked].every((id) => results.has(id))) {
        ws.close()
        return Promise.resolve(results)
      }

      return new Promise<Map<string, string>>((resolve, reject) => {
        resolveWait = (r) => {
          ws.close()
          resolve(r)
        }

        setTimeout(() => {
          const pending = [...tracked].filter((id) => !results.has(id))
          ws.close()
          reject(
            new Error(
              `waitForDocuments timed out after ${timeoutMs}ms. Still pending: ${pending.join(', ')}`
            )
          )
        }, timeoutMs)
      })
    },

    close() {
      ws.close()
    },
  }
}

// ── Message formatting (shared by TUI + CLI) ────────────────────────────────

export type MessageLevel = 'info' | 'success' | 'error' | 'warning'

export interface FormattedWsMessage {
  text: string
  level: MessageLevel
}

/** Check if a message is a notification (has sender/recipient, not a system message). */
function isNotification(msg: WebSocketServerMessage): msg is WsNotificationResponse {
  return 'sender' in msg && 'recipient' in msg
}

/**
 * Format a WebSocket message into display text + severity level.
 * Shared by TUI (toasts) and CLI (stderr lines) — single source of truth.
 */
export function formatWsMessage(msg: WebSocketServerMessage): FormattedWsMessage {
  if (isMessageType<WsTaskUpdateMessage>(msg, 'task_update')) {
    const progress = msg.progress > 0 ? ` (${Math.round(msg.progress)}%)` : ''
    const text = `${msg.file_name} — ${msg.status}${progress}`

    if (msg.status === 'completed') return { text, level: 'success' }
    if (msg.status === 'failed') return { text, level: 'error' }
    return { text, level: 'info' }
  }

  if (isMessageType<WsBatchCompleteMessage>(msg, 'batch_complete')) {
    const count = msg.doc_ext_ids.length
    return {
      text: `Batch complete: ${msg.batch_type} — ${count} doc${count !== 1 ? 's' : ''}`,
      level: 'success',
    }
  }

  if (isMessageType<WsErrorMessage>(msg, 'error')) {
    return { text: msg.message, level: 'error' }
  }

  if (isMessageType<WsPresenceUpdateMessage>(msg, 'presence_update')) {
    return { text: `${msg.user_id} is ${msg.status}`, level: 'info' }
  }

  if (isMessageType<WsResponseCompleteMessage>(msg, 'response_complete')) {
    const icon = msg.status === 'completed' ? '✓' : '✗'
    return {
      text: `${icon} Task ${msg.response_id} ${msg.status}`,
      level: msg.status === 'completed' ? 'success' : 'error',
    }
  }

  if (isNotification(msg)) {
    const sender = msg.sender?.email || 'someone'
    const content = msg.content ?? msg.type
    return { text: `${sender}: ${content}`, level: 'info' }
  }

  return { text: `${msg.type}`, level: 'info' }
}
